/**
 * backButton.js — make the browser's BACK close an open dialog instead of
 * leaving the page.
 *
 * Why this exists: Vuetify's overlays have a `closeOnBack` prop (default on)
 * that is meant to do exactly this, but it doesn't fire here. Its guard only
 * acts while a "popped" flag is set, and that flag is cleared by a
 * setTimeout(0) scheduled in the popstate handler — which runs BEFORE the
 * setTimeout its own router guard waits on, so by the time the guard looks
 * the flag is already false. Measured (2026-10-01, Vuetify 4.2.2): open the
 * episode info dialog in the Playlist, press back → the app navigates to the
 * previous page with the dialog still "open" in its singleton state.
 *
 * How it works:
 *   - a popstate listener records WHEN the last browser back/forward happened;
 *   - components register a "closer": a function that closes its topmost open
 *     dialog and returns true, or returns false when it has nothing open;
 *   - a router guard, on a navigation that started from that popstate, asks the
 *     closers (most recently registered first) and, if one closed something,
 *     cancels the navigation — vue-router then puts the URL back as it was.
 */

// ─── State ────────────────────────────────────────────────────────────────────

// Timestamp (ms) of the last popstate event. A timestamp rather than a boolean
// so it can never stay "stuck": only a navigation starting right after the
// event counts as a back press.
let lastPopstateAt = 0;

// How recent the popstate must be for a navigation to count as "back". The
// router guard runs a few milliseconds after the event; a user's next tap
// arrives far later than this.
const POPSTATE_WINDOW_MS = 400;

// Registered closers, in registration order. Iterated from the END so the most
// recently mounted component (usually the one on top) gets the first chance.
const closers = [];

// Remember when the browser fired back/forward. This listener MUST run before
// vue-router's own popstate listener: that one starts the navigation, and the
// guards run in the microtask checkpoint right after it returns — before any
// listener registered later gets the event. So it is registered HERE, at module
// evaluation: router/index.js imports this module, and imports are evaluated
// before the importing module's body, i.e. before createRouter() adds its own
// listener. Measured: registered inside installBackButton (after createRouter),
// even in the capture phase, the guard always saw a stale timestamp.
if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => {
    lastPopstateAt = Date.now();
  });
}

// ─── Registration API ─────────────────────────────────────────────────────────

/**
 * registerBackCloser(fn) — register a function called on a browser back.
 * `fn` must close whatever it has open and return true, or return false when
 * nothing of its own is open (the back then proceeds normally).
 * Returns an unregister function: call it in onUnmounted.
 */
export function registerBackCloser(fn) {
  // Store the closer
  closers.push(fn);
  // Unregister: remove exactly this closer (if still present)
  return () => {
    const i = closers.indexOf(fn);
    if (i !== -1) closers.splice(i, 1);
  };
}

// ─── Router wiring ────────────────────────────────────────────────────────────

/**
 * installBackButton(router) — wire the guard (the popstate listener is
 * already registered at module load, see above).
 * Called once, from router/index.js.
 */
export function installBackButton(router) {
  // Nothing to do outside a browser (SSR/tests)
  if (typeof window === 'undefined') return;

  // The guard: only for navigations triggered by back/forward
  router.beforeEach(() => {
    // Not a back/forward navigation (a link, a router.push) → let it through
    if (Date.now() - lastPopstateAt > POPSTATE_WINDOW_MS) return undefined;
    // Consume the event so one back press closes at most one thing
    lastPopstateAt = 0;
    // Ask the closers, topmost first
    for (let i = closers.length - 1; i >= 0; i--) {
      // A closer may throw if its component is half torn down: skip it
      let closed = false;
      try {
        closed = closers[i]() === true;
      } catch (err) {
        console.warn('backButton: closer failed', err);
      }
      // Something was closed → cancel the navigation (stay on this page)
      if (closed) return false;
    }
    // Nothing open → normal back navigation
    return undefined;
  });
}
